import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

declare const intlTelInput: any;

// intlTelInputUtils.validationError codes
const TOO_LONG = 3;
const INVALID_LENGTH = 5;

// A real ControlValueAccessor around intl-tel-input.
//
// This used to read the control off a plain `[form]`/`[controlName]` pair and sync itself via a
// manual `control.valueChanges` subscription. That subscription only fires when a write actually
// emits an event — but CompanyFormComponent patches an edit-mode company onto the form with
// `{ emitEvent: false }` (see `_tryPatchForm`), specifically so the dependency engine doesn't
// recompute mid-patch. Whether that silent patch landed before or after this component's own
// `ngAfterViewInit` was a race against the company/config HTTP responses, so the phone box came
// up blank on some page loads and correctly filled on others.
//
// A real ControlValueAccessor sidesteps the race: Angular's `FormControlName` directive calls
// `writeValue()` on every `setValue`/`patchValue`, silent or not (`emitEvent` only gates the
// `valueChanges`/`statusChanges` observables, never the accessor's own `writeValue`). Same fix
// FhSelectComponent applies to `one-select` for the same class of bug.
@Component({
  selector: 'app-phone-field',
  templateUrl: './phone-field.component.html',
  styleUrls: ['./phone-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneFieldComponent),
      multi: true,
    },
  ],
})
export class PhoneFieldComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  /* istanbul ignore next -- decorator metadata */
  @Input() required: boolean;

  /* istanbul ignore next -- decorator metadata */
  @ViewChild('phoneInput') phoneInput: ElementRef<HTMLInputElement>;

  focused = false;
  isDisabled = false;

  private iti: any;
  private lastAcceptedValue = '';
  // A value written before intl-tel-input exists (writeValue can land before ngAfterViewInit,
  // e.g. the control's initial FormBuilder value) is held here and applied once the widget is up.
  private _value: string | null = null;
  private _viewReady = false;
  private onFocus: () => void;
  private onInput: () => void;
  private onCountryChange: () => void;
  private onBlur: () => void;
  private onPaste: (event: Event) => void;

  ngAfterViewInit(): void {
    this.initPhoneInput();
    this._viewReady = true;
  }

  ngOnDestroy(): void {
    if (this.iti) {
      this.iti.destroy();
    }
    if (this.phoneInput?.nativeElement) {
      const input = this.phoneInput.nativeElement;
      if (this.onFocus) input.removeEventListener('focus', this.onFocus);
      if (this.onInput) input.removeEventListener('input', this.onInput);
      if (this.onCountryChange) input.removeEventListener('countrychange', this.onCountryChange);
      if (this.onBlur) input.removeEventListener('blur', this.onBlur);
    }
    if (this.onPaste) {
      document.removeEventListener('paste', this.onPaste, true);
    }
  }

  // ─── ControlValueAccessor ────────────────────────────────────────────────────────────────────

  writeValue(value: string | null): void {
    this._value = value ?? null;
    if (!this._viewReady || !this.iti) {
      // initPhoneInput reads `_value` once the widget is created.
      return;
    }
    this._writeToWidget(this._value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.phoneInput?.nativeElement) {
      this.phoneInput.nativeElement.disabled = isDisabled;
    }
  }

  // ─── Internals ───────────────────────────────────────────────────────────────────────────────

  private _writeToWidget(value: string | null): void {
    try {
      this.iti.setNumber(value || '');
    } catch (e) {}
    if (this.phoneInput?.nativeElement) {
      this.lastAcceptedValue = this.phoneInput.nativeElement.value;
    }
  }

  private initPhoneInput(): void {
    if (!this.phoneInput?.nativeElement) {
      return;
    }

    const input = this.phoneInput.nativeElement;

    this.iti = intlTelInput(input, {
      initialCountry: 'us',
      showSelectedDialCode: true,
    });
    input.disabled = this.isDisabled;

    if (this._value) {
      try {
        this.iti.setNumber(this._value);
      } catch (e) {}
    }
    this.lastAcceptedValue = input.value;

    const updateValue = () => {
      const phoneNumber = this.iti.getNumber();
      this._onTouched();
      this._onChange(phoneNumber);
    };

    this.onFocus = () => { this.focused = true; };

    const digitCount = (value: string) => value.replace(/\D/g, '').length;
    // type="tel" accepts any character; the widget only reformats digits, so
    // letters/symbols (e.g. pasted junk, or typed after a paste) pass straight
    // through untouched otherwise.
    const stripDisallowedChars = (value: string) => value.replace(/[^\d\s()+-]/g, '');

    this.onInput = () => {
      const sanitized = stripDisallowedChars(input.value);
      if (sanitized !== input.value) {
        input.value = sanitized;
      }

      const err = this.iti.getValidationError?.() ?? -1;
      const tooLong = err === TOO_LONG || err === INVALID_LENGTH;
      // Compare digit counts, not raw string length: formatting characters
      // (dashes/spaces) can appear or disappear between keystrokes (e.g. Venezuela's
      // "0412-1234567" losing its dash once it becomes too long), which would make a
      // plain input.value.length comparison miss an added digit.
      if (tooLong && digitCount(input.value) > digitCount(this.lastAcceptedValue)) {
        // reject the extra digit(s): revert to the last accepted (valid-length) value
        input.value = this.lastAcceptedValue;
      } else {
        this.lastAcceptedValue = input.value;
      }
      updateValue();
    };

    this.onCountryChange = () => {
      this.lastAcceptedValue = input.value;
      updateValue();
    };

    this.onBlur = () => {
      this.focused = false;
      updateValue();
    };

    // intl-tel-input v19.5.7 has a bug: its own paste handler defers a call to its
    // internal keyup handler via `setTimeout` with no event argument, which then
    // throws reading `e.inputType` on undefined instead of skipping gracefully - so
    // pasted numbers never get reformatted, and the exception shows up in the console.
    // Intercept the paste in the capture phase (before it reaches the library's own
    // bubble-phase listener on the input) and reformat it ourselves instead.
    this.onPaste = (event: Event) => {
      if (event.target !== input) {
        return;
      }
      event.stopImmediatePropagation();
      setTimeout(() => {
        try {
          const formatted = this.iti._formatNumberAsYouType?.();
          if (typeof formatted === 'string') {
            input.value = formatted;
          }
        } catch (e) {}
        this.onInput();
      }, 0);
    };

    input.addEventListener('focus', this.onFocus);
    input.addEventListener('input', this.onInput);
    input.addEventListener('countrychange', this.onCountryChange);
    input.addEventListener('blur', this.onBlur);
    document.addEventListener('paste', this.onPaste, true);
  }

  private _onChange: (value: string) => void = () => {};
  private _onTouched: () => void = () => {};
}
